import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, ChevronRight } from 'lucide-react';
import { dfwCities, getCitiesByCounty, counties } from '../data/dfwCities';
import { featuredCombinations, getCombinationBySlug } from '../data/serviceLocations';

const TXT = {
  en: {
    header: 'Areas We Serve in DFW',
    sub: 'Web Design, SEO & Digital Marketing Services Across the Dallas-Fort Worth Metroplex',
    servicesAcross: 'Our Services Across DFW',
    viewAll: 'View All DFW Locations & Services',
    counties: {
      'Dallas County': 'Dallas County',
      'Tarrant County': 'Tarrant County',
      'Collin County': 'Collin County',
      'Denton County': 'Denton County',
      'Other Areas': 'Other Areas',
    },
  },
  es: {
    header: 'Áreas que atendemos en DFW',
    sub: 'Diseño Web, SEO y Servicios de Marketing Digital en el Metroplex Dallas-Fort Worth',
    servicesAcross: 'Nuestros Servicios en DFW',
    viewAll: 'Ver todas las ubicaciones y servicios en DFW',
    counties: {
      'Dallas County': 'Condado de Dallas',
      'Tarrant County': 'Condado de Tarrant',
      'Collin County': 'Condado de Collin',
      'Denton County': 'Condado de Denton',
      'Other Areas': 'Otras Áreas',
    },
  },
};

const SERVICE_TITLE_ES = {
  'web-design': 'Diseño Web',
  'seo': 'Servicios SEO',
  'web-development': 'Desarrollo Web',
  'digital-marketing': 'Marketing Digital',
  'ecommerce': 'Soluciones eCommerce',
  'mobile-apps': 'Desarrollo de Apps',
  'ai-development': 'Desarrollo Web con IA',
  'local-seo': 'SEO Local',
};

function AreasServedFooter() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0] === 'es' ? 'es' : 'en';
  const tx = TXT[lang];
  // Group cities by county
  const citiesByCounty = {
    "Dallas County": getCitiesByCounty("Dallas County"),
    "Tarrant County": getCitiesByCounty("Tarrant County"),
    "Collin County": getCitiesByCounty("Collin County"),
    "Denton County": getCitiesByCounty("Denton County"),
    "Other Areas": getCitiesByCounty("Other Areas")
  };

  // Get featured service-location combinations
  const featuredServices = featuredCombinations
    .map(slug => getCombinationBySlug(slug))
    .filter(Boolean);

  return (
    <section className="bg-slate-800 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <MapPin className="h-6 w-6 text-blue-400" />
            <h2 className="text-2xl font-bold text-white">{tx.header}</h2>
          </div>
          <p className="text-slate-400 text-sm">
            {tx.sub}
          </p>
        </div>

        {/* Cities Grid by County */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {Object.entries(citiesByCounty).map(([county, cities]) => (
            <div key={county}>
              <h3 className="text-blue-400 font-semibold text-sm mb-3 uppercase tracking-wide">
                {tx.counties[county] || county}
              </h3>
              <ul className="space-y-1">
                {cities.slice(0, 10).map((city) => (
                  <li key={city.slug}>
                    <Link 
                      to={`/locations/${city.slug}`}
                      className="text-slate-300 hover:text-white text-sm transition-colors flex items-center gap-1 group"
                    >
                      <span>{city.name}</span>
                      <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Service Keywords - Now with Links! */}
        <div className="mt-10 pt-8 border-t border-slate-700">
          <h3 className="text-white font-semibold text-sm mb-4 text-center">{tx.servicesAcross}</h3>
          <div className="flex flex-wrap justify-center gap-2">
            {featuredServices.map((combo) => (
              <Link 
                key={combo.slug}
                to={`/services/${combo.slug}`}
                className="text-xs text-slate-400 bg-slate-700/50 px-2 py-1 rounded hover:bg-blue-600 hover:text-white transition-colors"
              >
                {lang === 'es'
                  ? `${SERVICE_TITLE_ES[combo.service.id] || combo.service.title} ${combo.location.city}`
                  : combo.title}
              </Link>
            ))}
          </div>
        </div>

        {/* View All Link */}
        <div className="text-center mt-8">
          <Link 
            to="/locations/dallas-fort-worth"
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium text-sm"
          >
            {tx.viewAll}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default AreasServedFooter;
